/**
 * Format a date as YYYY-MM-DD using local timezone.
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string as a local midnight Date.
 * Throws if the input is not a valid calendar date.
 */
export function parseLocalDate(dateString: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new Error(`Invalid date string "${dateString}": expected format YYYY-MM-DD`);
  }

  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error(`Invalid calendar date "${dateString}": out-of-range month or day`);
  }

  return date;
}

/**
 * Calculate days until a date (negative if past)
 */
export function daysUntil(date: Date): number {
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate days until expiry from a YYYY-MM-DD date string.
 * Parses as local midnight to avoid timezone off-by-one errors.
 *
 * @overload With a non-nullable string, returns number.
 * @overload With a nullable string, returns number | null.
 */
export function getDaysUntilExpiry(expiryDate: string): number;
export function getDaysUntilExpiry(expiryDate: string | null | undefined): number | null;
export function getDaysUntilExpiry(expiryDate: string | null | undefined): number | null {
  if (!expiryDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = parseLocalDate(expiryDate);
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format a YYYY-MM-DD date string for display (e.g., "Dec 15", "Jan 3").
 * Parses as local midnight to avoid timezone off-by-one errors.
 */
export function formatExpiryDate(expiryDate: string, locale?: string): string {
  const date = parseLocalDate(expiryDate);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}
